import {
  AssetFinancialHoldingKind,
  ClaimCollateralRequest,
  ClaimCollateralResponse,
  ClaimCollateralType,
  ProceedingParticipantType,
} from "../../../../../api/ClientFileApi";
import { ClaimStatusKey } from "../../../../claimStatus";
import {
  ClaimCollateralFormValues,
  ClaimCollateralHoldingFormValues,
  ClaimFormValues,
  ExpectedLossPercentRange,
  NEW_HOLDING_OPTION_ID,
} from "../../ClaimFormFields.types";

// A new holding is preset to Austria; a plain name, since the country (Land) list is a typing aid.
const AustriaCountry = "Österreich";

export type HoldingCollateralType = Extract<
  ClaimCollateralType,
  "BuildingSavingsContract" | "LifeInsurance" | "PensionInsurance" | "SavingsAccount" | "Securities"
>;

export type FreeTextCollateralType = Exclude<ClaimCollateralType, HoldingCollateralType>;

export type HoldingNumberLabel = "accountNumber" | "depotNumber" | "policyNumber";

export interface HoldingCollateralConfig {
  kind: AssetFinancialHoldingKind;
  participantType: ProceedingParticipantType;
  numberLabel: HoldingNumberLabel;
}

/** The types of security that refer to an asset of the asset schedule, and which asset they refer to. */
export const HOLDING_COLLATERAL_TYPES: Record<HoldingCollateralType, HoldingCollateralConfig> = {
  BuildingSavingsContract: {
    kind: AssetFinancialHoldingKind.BuildingSavingsContract,
    participantType: ProceedingParticipantType.Bank,
    numberLabel: "accountNumber",
  },
  LifeInsurance: {
    kind: AssetFinancialHoldingKind.LifeInsurance,
    participantType: ProceedingParticipantType.Insurance,
    numberLabel: "policyNumber",
  },
  PensionInsurance: {
    kind: AssetFinancialHoldingKind.PensionInsurance,
    participantType: ProceedingParticipantType.Insurance,
    numberLabel: "policyNumber",
  },
  SavingsAccount: {
    kind: AssetFinancialHoldingKind.SavingsAccount,
    participantType: ProceedingParticipantType.Bank,
    numberLabel: "accountNumber",
  },
  Securities: {
    kind: AssetFinancialHoldingKind.SecuritiesDepot,
    participantType: ProceedingParticipantType.Bank,
    numberLabel: "depotNumber",
  },
};

/** Every type, in the order the dialog offers them. */
export const COLLATERAL_TYPE_ORDER: ClaimCollateralType[] = [
  ClaimCollateralType.BuildingSavingsContract,
  ClaimCollateralType.LifeInsurance,
  ClaimCollateralType.PensionInsurance,
  ClaimCollateralType.SavingsAccount,
  ClaimCollateralType.Securities,
  ClaimCollateralType.RealEstate,
  ClaimCollateralType.WagePledge,
  ClaimCollateralType.Other,
];

export function isHoldingCollateralType(type: ClaimCollateralType | null): type is HoldingCollateralType {
  return type !== null && type in HOLDING_COLLATERAL_TYPES;
}

export function createEmptyCollateralHolding(): ClaimCollateralHoldingFormValues {
  return {
    institutionName: null,
    accountNumber: null,
    street: null,
    houseNumber: null,
    zipCode: null,
    city: null,
    country: AustriaCountry,
    isValueKnown: null,
    value: null,
    valueDate: null,
    remarks: null,
  };
}

export function createEmptyCollateral(): ClaimCollateralFormValues {
  return {
    key: crypto.randomUUID(),
    id: null,
    type: null,
    holdingSelection: null,
    newHolding: createEmptyCollateralHolding(),
    details: null,
  };
}

export function fromCollateralResponses(
  collaterals: ClaimCollateralResponse[] | undefined
): ClaimCollateralFormValues[] {
  return (collaterals ?? []).map((collateral) => ({
    ...createEmptyCollateral(),
    key: collateral.id,
    id: collateral.id,
    type: collateral.type,
    holdingSelection: collateral.holdingId ?? null,
    details: collateral.details ?? null,
  }));
}

/**
 * The securities of the request. A claim keeps them only while "Gibt es Sicherheiten?" is answered with Ja,
 * and every security carries only the field its type asks for.
 */
export function toCollateralRequests(values: ClaimFormValues): ClaimCollateralRequest[] {
  if (values.collateralStatus !== ClaimStatusKey.Yes) {
    return [];
  }

  return values.collaterals.flatMap<ClaimCollateralRequest>((collateral) => {
    if (collateral.type === null) {
      return [];
    }

    if (!isHoldingCollateralType(collateral.type)) {
      return [
        { id: collateral.id, type: collateral.type, holdingId: null, newHolding: null, details: collateral.details },
      ];
    }

    const createsHolding = collateral.holdingSelection === NEW_HOLDING_OPTION_ID;
    const holding = collateral.newHolding;
    const isValueKnown = holding.isValueKnown === true;

    return [
      {
        id: collateral.id,
        type: collateral.type,
        holdingId: createsHolding ? null : collateral.holdingSelection,
        newHolding: createsHolding
          ? {
              institutionName: holding.institutionName,
              accountNumber: holding.accountNumber,
              street: holding.street,
              houseNumber: holding.houseNumber,
              zipCode: holding.zipCode,
              city: holding.city,
              country: holding.country,
              isValueKnown: holding.isValueKnown,
              value: isValueKnown ? holding.value : null,
              valueDate: isValueKnown ? holding.valueDate : null,
              remarks: holding.remarks,
            }
          : null,
        details: null,
      },
    ];
  });
}

/** The expected loss of the request; only a claim with securities has one. */
export function toExpectedLossPercent(values: ClaimFormValues): number | null {
  return values.collateralStatus === ClaimStatusKey.Yes ? values.expectedLossPercent : null;
}

/**
 * The form after "Gibt es Sicherheiten?" changed: Ja opens the first security block and assumes a total loss
 * until the value is lowered, every other answer drops the securities and the expected loss.
 */
export function collateralValuesFor(
  collateralStatus: string | null,
  current: Pick<ClaimFormValues, "collaterals" | "expectedLossPercent">
): Pick<ClaimFormValues, "collaterals" | "expectedLossPercent"> {
  if (collateralStatus !== ClaimStatusKey.Yes) {
    return { collaterals: [], expectedLossPercent: null };
  }

  return {
    collaterals: current.collaterals.length > 0 ? current.collaterals : [createEmptyCollateral()],
    expectedLossPercent: current.expectedLossPercent ?? ExpectedLossPercentRange.default,
  };
}
